import { Component, Input, OnInit, ViewChild, HostListener, OnDestroy } from "@angular/core";
import { PopoverController } from "@ionic/angular";
import { IQConnectDeviceComponent } from "../iq-connect-device/iq-connect-device.component";
import { FacilityModel } from "src/app/api/proxy/practice/models/facility-model";
import { Events } from "src/app/services/events/events";
import { PractitionerService } from "src/app/services/practitioner-service/practitioner.service";
import { map, Subscription } from "rxjs";
import { ConnectivityService } from "src/app/services/connectivity-service/connectivity-service";
import { TreatmentService } from "src/app/services/treatment-service/treatment.service";
import { TreatmentModeType } from "src/app/pages/treatment/treatment.page";
import { TranslatorService } from "src/app/services/translator-service/translator.service";
import { EnvironmentConfigService } from "src/app/services/environment-config-service/environment-config-service";
import { DeviceSessionMessageService } from "src/app/services/device-session-message-service/device-session-message.service";
import { DeviceSessionMessagesPopoverComponent } from "./device-session-messages-popover.component";

@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  standalone: false
})
export class HeaderComponent implements OnInit, OnDestroy {
  @ViewChild('iqDevice') iqDevice?: IQConnectDeviceComponent;
  @Input() title: string | null | undefined = "";
  @Input() menuPages: MenuPage[] = [];
  @Input() actionButtons: MenuActionButton[] = [];
  /** Non-clickable status text shown in the action-button slot (e.g. in-progress queue). */
  @Input() statusBanner: string | null | undefined;
  @Input() onClose?: () => Promise<void> = undefined;
  @Input() navLine1: string | null | undefined;
  @Input() navLine2: string | null | undefined;
  @Input() navigationEnabled: boolean = false;
  @Input() navDisabledPrev = false;
  @Input() navDisabledNext = false;
  @Input() showCurrentVisitBadge = false;
  @Input() onPrev?: () => Promise<void>;
  @Input() onNext?: () => Promise<void>;
  @Input() isWelcomePage: boolean = false;
  @Input() isTreatmentPage: boolean = false;
  @Input() isProfilePage: boolean = false;
  @Input() showAdjustDeviceControls: boolean = false;
  @Input() allowWebConnectButton = false;
  @Input() showDeviceSessionMessages: boolean = false;
  activePage: MenuPage | null = null;
  serialNumber: string | null = null;
  displayBoardFw: string | null = null;
  mainBoardFw: string | null = null;
  deviceConnected: boolean = false;
  isOnline$ = this.connectivity.connectivity$.pipe(map(s => s.connected));
  deviceMessagesCount$ = this.deviceSessionMessageService.messages$.pipe(map(m => m.length));
  /** Portrait / compact mode flag */
  isCompact = false;
  /** Media query listener (orientation) */
  private mql?: MediaQueryList;
  private mqlHandler = (e: MediaQueryListEvent) => this.computeCompact();
  // ===== Facility selection =====
  // facilityForm!: UntypedFormGroup;
  facilities!: FacilityModel[];
  selectedFacility!: FacilityModel | null;
  // facilitySelectEnabled: boolean = false;
  subscriptions: Subscription[] = [];

  adjustmentModeText: string = '';

  public get allowConnectButton(): boolean {
    return this.envService.Platform === 'android' || this.allowWebConnectButton;
  }

  public get isDev(): boolean {
    return this.envService.isDev;
  }

  public get showSideMenuButton(): boolean {
    return this.envService.showDevSideMenu;
  }

  /** True when every menu tab is disabled (e.g. tab navigation locked). */
  get areMenuPagesDisabled(): boolean {
    return this.menuPages.length > 0 && this.menuPages.every((p) => p.isDisabled);
  }

  public get serialNumberDisplay(): string {
    if (!this.serialNumber) {
      return '';
    }

    const displayFw = this.displayBoardFw;
    const mainFw = this.mainBoardFw;
    if (this.isValidFirmwareVersion(displayFw) && this.isValidFirmwareVersion(mainFw)) {
      return `SN: ${this.serialNumber} (${displayFw} - ${mainFw})`;
    }

    return `SN: ${this.serialNumber}`;
  }

  constructor(
    protected practitionerService: PractitionerService,
    protected treatmentService: TreatmentService,
    protected _translatorService: TranslatorService,
    private connectivity: ConnectivityService,
    private envService: EnvironmentConfigService,
    private deviceSessionMessageService: DeviceSessionMessageService,
    private popoverController: PopoverController,
  ) {

    this.subscriptions.push(Events.facilitiesUpdated.subscribe(async () => {
      await this.updateFacilityData();
    }));
  }

  async ngOnInit() {
    await this.updateFacilityData();
    // Default: first page active
    if (this.menuPages.length > 0) {
      this.activePage = this.menuPages[0];
    }
    // Initial compact computation + listeners
    this.computeCompact();
    this.mql = window.matchMedia('(orientation: portrait)');
    this.mql.addEventListener?.('change', this.mqlHandler);

    if (this.isTreatmentPage) {
      this.setTreatmentModeText();
      this.subscriptions.push(
        this.treatmentService.adjustMode$.subscribe(() => {
          this.setTreatmentModeText();
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.mql?.removeEventListener?.('change', this.mqlHandler);
    this.subscriptions.forEach((a) => { a.unsubscribe() });
  }

  /** Also recompute on resize */
  @HostListener('window:resize')
  onResize() {
    this.computeCompact();
  }

  /** Decide when to switch to compact (portrait or narrow) */
  private computeCompact() {
    const isPortrait = window.matchMedia('(orientation: portrait)').matches;
    const isNarrow = window.innerWidth <= 900; // aligns with your CSS breakpoint
    this.isCompact = isPortrait || isNarrow;
  }

  async pageSelect(page: MenuPage) {
    if (page.isDisabled) {
      return;
    }

    if (this.activePage === page && page.onSecondarySelect) {
      await page.onSecondarySelect();
    } else if (page.onSelect) {
      await page.onSelect();
    }
  }

  async setCurrentPage(page: MenuPage) {
    this.activePage = page;
  }

  async actionButtonClick(button: MenuActionButton) {
    if (button.onSelect) {
      await button.onSelect();
    }
  }

  // ===== Dropdown handlers (portrait/compact) =====
  async onPageDropdown(title: string) {
    const page = this.menuPages.find(p => p.title === title);
    if (page && !page.isDisabled) {
      await this.pageSelect(page);
    }
  }

  async onActionDropdown(title: string) {
    const btn = this.actionButtons.find(b => b.title === title);
    if (btn) {
      await this.actionButtonClick(btn);
      // Optional: if you want the select to "reset" after firing, you could manage a
      // local binding for the value and set it back to undefined here.
    }
  }

  async prevClicked() { if (this.onPrev) { await this.onPrev(); } }
  async nextClicked() { if (this.onNext) { await this.onNext(); } }

  async closeClicked() {
    if (this.onClose) {
      await this.onClose();
    }
  }

  async handleSerial(serial: string | null) {
    this.serialNumber = serial;
  }

  handleDisplayBoardFirmwareVersion(version: string | null) {
    this.displayBoardFw = version;
  }

  handleMainBoardFirmwareVersion(version: string | null) {
    this.mainBoardFw = version;
  }

  async handleConnected(connected: boolean) {
    this.deviceConnected = connected;
    if (!connected) {
      this.clearDeviceInfo();
    }
  }

  async handleDisconnected(connected: boolean) {
    this.clearDeviceInfo();
    this.deviceConnected = connected;
  }

  private clearDeviceInfo(): void {
    this.serialNumber = null;
    this.displayBoardFw = null;
    this.mainBoardFw = null;
    this.deviceSessionMessageService.clearAll();
  }

  private isValidFirmwareVersion(version: string | null): boolean {
    if (!version) {
      return false;
    }

    const numericVersion = parseFloat(version);
    return !Number.isNaN(numericVersion) && numericVersion !== 0;
  }

  async toggleConnection() {
    this.iqDevice?.toggleConnection();
  }

  connect() {
    this.iqDevice?.connect();
  }

  async disconnect() {
    await this.iqDevice?.disconnect();
  }

  async openDeviceMessages(event: Event): Promise<void> {
    const popover = await this.popoverController.create({
      component: DeviceSessionMessagesPopoverComponent,
      event,
      translucent: false,
      cssClass: 'device-messages-popover-overlay',
      side: 'left',
      alignment: 'start',
    });
    await popover.present();
  }

  async onTreatmentModeChanged() {
    const currentModeIndex = this.treatmentService.adjustmentMode;
    if (currentModeIndex + 1 > Object.keys(TreatmentModeType).length / 2 - 1) {
      this.treatmentService.adjustmentMode = TreatmentModeType.Append;
    } else {
      this.treatmentService.adjustmentMode = currentModeIndex + 1;
    }

    await this.treatmentService.saveAdjustmentMode();

    this.setTreatmentModeText();
  }

  setTreatmentModeText() {
    let text = '';
    if (this.treatmentService.adjustmentMode === TreatmentModeType.Advance) {
      text = this._translatorService.translateText('treatment-page.menu-pages.adjust-advance');
    } else if (this.treatmentService.adjustmentMode === TreatmentModeType.Append) {
      text = this._translatorService.translateText('treatment-page.menu-pages.adjust-append');
    }

    this.adjustmentModeText = text;
  }

  async onFacilityChange(event: CustomEvent<{ value: FacilityModel }>): Promise<void> {
    this.selectedFacility = event.detail.value;
    await this.practitionerService.selectFacility(this.selectedFacility!);
    Events.facilitySelected.emit(this.selectedFacility!);
  }

  private async updateFacilityData() {
    this.facilities = await this.practitionerService.getFacilities();
    this.practitionerService.getSelectedFacility().then((selected) => {
      this.selectedFacility = selected
        ? (this.facilities.find(f => f.id === selected.id) ?? null)
        : null;
      Events.facilitySelected.emit(this.selectedFacility || undefined);
    });
  }
}

export class MenuPage {
  constructor(
    public title: string,
    public onSelect?: () => Promise<void>,   // async-capable callback
    public onSecondarySelect?: () => Promise<void>   // async-capable callback
  ) { }

  public isDisabled: boolean = false;
}

export class MenuActionButton {
  constructor(
    public title: string,
    public onSelect?: () => Promise<void>   // async-capable callback
  ) { }
}